import cn from 'classnames';
import Image from 'next/image';
import React from 'react';

import MaxWidth from '@/components/common/layout/MaxWidth';

import css from './SeasonalGuideSectionHeading.module.css';
import { TSeasonalGuideSectionHeading } from './types';

type TSeasonalGuideSectionHeadingProps = TSeasonalGuideSectionHeading & {
  tag: string;
};

export const SeasonalGuideSectionHeading = ({
  tag,
  icon,
  title,
  description,
  textColor,
  linkText,
  linkBackgroundColor,
  linkTextColor,
  linkUrl,
  imageUrl,
  imageBlurDataUrl,
  imagePosition,
  backgroundUrl,
  backgroundImageOpacity,
  backgroundColor,
}: TSeasonalGuideSectionHeadingProps) => {
  return (
    <div className={cn('relative', css.root)}>
      <div className="absolute inset-0 z-0" style={{ backgroundColor }}>
        {backgroundUrl && (
          <Image
            src={backgroundUrl}
            alt=""
            layout="fill"
            objectFit="cover"
            objectPosition="center"
            loading="lazy"
            quality={25}
            style={{ opacity: backgroundImageOpacity }}
          />
        )}
      </div>

      <div className="relative overflow-clip">
        <MaxWidth className="relative pt-4 pb-32 mx-auto lg:pb-52 md:pt-10 lg:pt-28">
          <div className="grid lg:grid-cols-2 lg:gap-x-24">
            <div className="order-2 mb-4 col-span-2 lg:order-1 lg:mb-0">
              <div
                className={cn('flex lg:absolute lg:-translate-y-1/2 lg:top-14', {
                  'lg:left-4': imagePosition === 'left',
                  'lg:right-4': imagePosition === 'right',
                })}>
                <span
                  className={cn('px-4 py-2 border-2 uppercase', css.tag)}
                  style={{ color: textColor, borderColor: textColor }}>
                  {tag}
                </span>
              </div>
            </div>

            <div
              className={cn('flex flex-col justify-center order-1 mb-10 lg:mb-0', {
                'lg:order-3': imagePosition === 'right',
                'lg:order-2': imagePosition === 'left',
              })}>
              <div
                className={cn('relative w-full h-[284px] md:h-[400px] lg:h-[700px]', {
                  [css.parallaxImageLeft as string]: imagePosition === 'left',
                  [css.parallaxImageRight as string]: imagePosition === 'right',
                })}>
                <div
                  className={cn('absolute inset-0 overflow-clip rounded-3xl', {
                    '-ml-10 xl:-ml-28 xxxl:-ml-72': imagePosition === 'left',
                    '-mr-10 xl:-mr-28 xxxl:-mr-72': imagePosition === 'right',
                  })}>
                  <Image
                    alt=""
                    src={imageUrl}
                    role="presentation"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    placeholder={imageBlurDataUrl ? 'blur' : 'empty'}
                    blurDataURL={imageBlurDataUrl}
                    quality={45}
                  />
                </div>
              </div>
            </div>

            <div
              className={cn('flex flex-col justify-center gap-y-4 order-3', {
                'lg:order-2': imagePosition === 'right',
                'lg:order-3': imagePosition === 'left',
              })}
              style={{ color: textColor }}>
              <div className={cn('hidden lg:block', css.icon)}>{icon}</div>
              <div className={css.title}>{title}</div>
              <div className={css.description}>{description}</div>
              <div className={css.link}>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={linkUrl}
                  className="px-6 py-3 rounded-3xl"
                  style={{
                    backgroundColor: linkBackgroundColor,
                    color: linkTextColor,
                  }}>
                  {linkText}
                </a>
              </div>
            </div>
          </div>
        </MaxWidth>
      </div>
    </div>
  );
};
